import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Logo from '../../components/feature/Logo';

export default function Partnership() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    partnerType: '',
    educationType: '',
    desiredFeatures: '',
    budget: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setFormStatus('sending');
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => params.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d714h5k991ove783sogg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', partnerType: '', message: '' });
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const benefits = [
    {
      icon: 'ri-line-chart-line',
      color: 'from-teal-500 to-emerald-500',
      title: 'Mehr Umsatz pro Schüler',
      items: [
        'Zusätzliche Einnahmequelle durch digitale Lernangebote',
        'Upsell-Möglichkeiten (Prüfungsvorbereitung, Intensivprogramme)',
        'Kombination Nachhilfe + KI = höherer wahrgenommener Wert',
      ],
      result: 'Mehr Umsatz bei bestehenden Kunden',
    },
    {
      icon: 'ri-time-line',
      color: 'from-orange-500 to-amber-500',
      title: 'Weniger Aufwand, mehr Skalierung',
      items: [
        'Schüler lernen selbstständig zwischen Unterrichtseinheiten',
        'Weniger Wiederholungen von Grundlagen im Unterricht',
        'Fokus auf individuelle Betreuung statt Standard-Erklärungen',
      ],
      result: 'Mehr Schüler gleichzeitig ohne Mehraufwand',
    },
    {
      icon: 'ri-award-line',
      color: 'from-indigo-500 to-violet-500',
      title: 'Bessere Ergebnisse, zufriedenere Kunden',
      items: [
        'Schüler erhalten sofort Feedback und verständliche Erklärungen',
        'Fokus auf echtes Verständnis statt Auswendiglernen',
        'Lernfortschritte werden sichtbar und messbar',
      ],
      result: 'Höhere Erfolgsquote & langfristige Kundenbindung',
    },
    {
      icon: 'ri-robot-2-line',
      color: 'from-teal-500 to-cyan-500',
      title: 'Ihr digitaler Assistent',
      items: [
        'Automatische Analyse von Aufgaben und Antworten',
        'Unterstützung bei der Vorbereitung von Unterrichtseinheiten',
        'Strukturierte Einblicke in den Lernstand Ihrer Schüler',
      ],
      result: 'Sie werden effizienter und stärker in Ihrer Rolle',
    },
    {
      icon: 'ri-global-line',
      color: 'from-emerald-500 to-green-500',
      title: '24/7 Lernsupport für Ihre Schüler',
      items: [
        'Lernen jederzeit möglich – auch außerhalb Ihrer Zeiten',
        'Kein Leerlauf zwischen Nachhilfestunden',
        'Kontinuierlicher Lernfluss statt punktueller Betreuung',
      ],
      result: 'Sie bieten ein modernes Premium-Lernsystem',
    },
  ];

  const partnerModels = [
    {
      icon: 'ri-medal-line',
      color: 'from-teal-500 to-emerald-600',
      bg: 'from-teal-50 to-emerald-50',
      border: 'border-teal-200',
      badge: 'Maximale Flexibilität',
      title: 'White-Label',
      subtitle: 'Eigengebrandete Version unter Ihrer Marke',
      description:
        'Vermarkten Sie Learnsnap komplett unter Ihrer eigenen Marke, mit eigener Domain und vollständig individualisierter Oberfläche.',
      features: [
        'Eigene Domain & vollständiges Branding',
        'Alle Features inklusive – individuell anpassbar',
        'Eigene Preisgestaltung für Ihre Kunden',
        'White-Label Support & Onboarding',
        'Technischer Betrieb durch Learnsnap',
      ],
      ideal: 'Nachhilfeinstitute, Bildungsunternehmen, Akademien',
    },
    {
      icon: 'ri-store-2-line',
      color: 'from-orange-500 to-amber-500',
      bg: 'from-orange-50 to-amber-50',
      border: 'border-orange-200',
      badge: 'Maximale Reichweite',
      title: 'Franchise',
      subtitle: 'Vermarktung unter dem Learnsnap Brand',
      description:
        'Profitieren Sie von der etablierten Marke Learnsnap und vermarkten Sie als Franchise-Partner entweder unter Learnsnap oder als eigene White-Label-Version.',
      features: [
        'Nutzung der Learnsnap-Marke & Bekanntheit',
        'Wahlweise White-Label oder Learnsnap Brand',
        'Exklusives Territorium nach Vereinbarung',
        'Vollständiges Marketing-Material',
        'Laufende Unterstützung durch Learnsnap',
      ],
      ideal: 'Bildungsunternehmer, Investoren, regionale Bildungspartner',
    },
    {
      icon: 'ri-team-line',
      color: 'from-violet-500 to-purple-500',
      bg: 'from-violet-50 to-purple-50',
      border: 'border-violet-200',
      badge: 'Einfacher Einstieg',
      title: 'Partner',
      subtitle: 'Nutzung für eigene Kunden im Learnsnap Brand',
      description:
        'Der einfachste Weg zur Partnerschaft: Nutzen Sie Learnsnap für Ihre bestehenden Schüler und Kunden direkt unter dem Learnsnap Brand.',
      features: [
        'Schnellster Einstieg – sofort einsatzbereit',
        'Attraktive Partner-Konditionen & Rabatte',
        'Nutzung für eigene Schüler & Kunden',
        'Dedizierter Partner-Support',
        'Gemeinsame Vermarktung möglich',
      ],
      ideal: 'Einzeltutoren, kleine Nachhilfeinstitute, Lehrkräfte',
    },
  ];

  const revenueModels = [
    { icon: 'ri-refresh-line', label: 'Monatliche Schüler-Abos', desc: 'Wiederkehrende Einnahmen' },
    { icon: 'ri-vip-crown-line', label: 'Premium-Zugänge', desc: 'Zusatzprogramme & Features' },
    { icon: 'ri-building-line', label: 'Lizenzmodelle', desc: 'Institute & Schulen' },
    { icon: 'ri-mix-line', label: 'Hybrid-Modell', desc: 'Offline + Online kombiniert' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/partnerschaft`,
                "url": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/partnerschaft`,
                "name": "LearnSnap Partnerschaft – KI-Bildungsplattform für Bildungseinrichtungen",
                "description": "Werden Sie Partner von LearnSnap. White-Label, Franchise oder Partnermodell für Nachhilfeinstitute, Einzeltutoren und Bildungseinrichtungen.",
                "inLanguage": "de",
                "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/#website` }
              },
              {
                "@type": "Service",
                "name": "LearnSnap Partnerschaft",
                "serviceType": "KI-Bildungsplattform als White-Label, Franchise oder Partnermodell",
                "url": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/partnerschaft`,
                "description": "Learnsnap bietet Bildungseinrichtungen eine skalierbare KI-Lernplattform als White-Label, Franchise oder Partnermodell – individuell bepreist nach Setup-Gebühr und monatlicher Support-Fee.",
                "inLanguage": "de",
                "areaServed": {
                  "@type": "Country",
                  "name": "Deutschland"
                },
                "provider": {
                  "@type": "Organization",
                  "name": "Future Now UG",
                  "url": import.meta.env.VITE_SITE_URL || 'https://learnsnap.online',
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Max-Planck-Str. 4",
                    "addressLocality": "Aschheim",
                    "postalCode": "85609",
                    "addressCountry": "DE"
                  }
                },
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "Partnerschaftsmodelle",
                  "itemListElement": [
                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "White-Label" } },
                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Franchise" } },
                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Partner" } }
                  ]
                }
              }
            ]
          })
        }}
      />
      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/">
              <Logo size="md" showText={true} textColor={isScrolled ? 'text-slate-900' : 'text-white'} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className={`text-sm font-medium hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-slate-700' : 'text-white'
                }`}
              >
                Startseite
              </Link>
              <Link
                to="/pricing"
                className={`text-sm font-medium hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-slate-700' : 'text-white'
                }`}
              >
                Preise
              </Link>
              <a
                href="#partner-models"
                className={`text-sm font-medium hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-slate-700' : 'text-white'
                }`}
              >
                Modelle
              </a>
              <a
                href="#apply"
                className="bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap"
              >
                Jetzt bewerben
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/1e6ae7258cde023af4b4f1225842d6b5.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-teal-900/50 to-slate-900/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center w-full">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-5 py-2.5 rounded-full text-white mb-8">
            <i className="ri-sparkling-line text-teal-300" />
            <span className="text-sm font-semibold">KI-Bildung – der Wachstumsmarkt der Zukunft</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Ihr Wettbewerbs&shy;vorteil<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-emerald-400">
              im Bildungsmarkt
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-white/85 mb-10 max-w-3xl mx-auto leading-relaxed">
            Learnsnap ist die KI-gestützte Lernplattform, die Ihr bestehendes Bildungsangebot nicht ersetzt –
            sondern gezielt verstärkt, ergänzt und skalierbar macht.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#apply"
              className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold text-base hover:shadow-2xl transition-all cursor-pointer whitespace-nowrap"
            >
              Jetzt Partner werden
            </a>
            <a
              href="#partner-models"
              className="bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-xl font-semibold text-base hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
            >
              Modelle entdecken
            </a>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white/60 text-3xl" />
        </div>
      </section>

      {/* Learnsnap replaces nobody banner */}
      <section className="bg-gradient-to-r from-teal-600 to-emerald-600 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-10">
            <div className="flex items-center gap-3 text-white">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-check-double-line text-xl" />
              </div>
              <span className="font-semibold">Learnsnap ersetzt keine Lehrpersonen</span>
            </div>
            <div className="hidden md:block w-px h-8 bg-white/30" />
            <div className="flex items-center gap-3 text-white">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-rocket-line text-xl" />
              </div>
              <span className="font-semibold">Learnsnap macht Sie effizienter & stärker</span>
            </div>
            <div className="hidden md:block w-px h-8 bg-white/30" />
            <div className="flex items-center gap-3 text-white">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-line-chart-line text-xl" />
              </div>
              <span className="font-semibold">Wachstumsmarkt KI – jetzt positionieren</span>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Market Intro */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-global-line" />
                Strategische Positionierung
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                Positionieren Sie sich im schnellst wachsenden Markt mit KI
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Der Bildungsmarkt befindet sich aktuell in einem fundamentalen Wandel. Wer sich jetzt mit KI
                positioniert, sichert sich einen entscheidenden Wettbewerbsvorteil und spart sich Zeit und Geld
                für die eigene Entwicklung.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-parent-line', text: 'Eltern erwarten digitale Lösungen und messbare Ergebnisse' },
                  { icon: 'ri-smartphone-line', text: 'Schüler sind KI bereits gewohnt und verlangen moderne Lernmethoden' },
                  { icon: 'ri-bar-chart-box-line', text: 'Klassische Nachhilfe allein wird langfristig weniger wettbewerbsfähig' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-teal-600 text-lg`} />
                    </div>
                    <p className="text-slate-700 leading-relaxed pt-1">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-6 border border-teal-200">
                <p className="font-semibold text-teal-800 mb-3">Mit Learnsnap positionieren Sie sich als:</p>
                <div className="flex flex-wrap gap-3">
                  {['Innovativer Anbieter', 'Moderner Bildungspartner', 'KI-Pionier'].map((tag) => (
                    <span
                      key={tag}
                      className="bg-teal-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-teal-100">
                <img
                  src="https://readdy.ai/api/search-image?query=business%20woman%20education%20entrepreneur%20confidently%20presenting%20digital%20learning%20platform%20on%20large%20screen%20to%20small%20group%20modern%20bright%20conference%20room%20charts%20showing%20growth%20statistics%20light%20skin%20tones%20professional%20business%20casual%20contemporary%20office%20natural%20daylight%20innovative%20edtech%20startup%20environment&width=800&height=700&seq=partnership-market-2025&orientation=portrait"
                  alt="Learnsnap Partnerschaft – Bildungsmarkt"
                  className="w-full h-full object-top object-cover"
                  style={{ height: '500px' }}
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                    <i className="ri-line-chart-line text-teal-600 text-2xl" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-lg">+47%</p>
                    <p className="text-xs text-slate-500">KI-Bildungsmarkt Wachstum p.a.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-gift-line" />
              Ihr konkreter Nutzen als Partner
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Was Sie mit Learnsnap gewinnen
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Fünf konkrete Vorteile, die Ihr Bildungsangebot sofort stärken
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((b, i) => (
              <div
                key={i}
                className={`rounded-2xl p-8 border-2 ${
                  i === 4 ? 'md:col-span-2 lg:col-span-1' : ''
                } hover:shadow-xl transition-all bg-white border-slate-100`}
              >
                <div
                  className={`w-14 h-14 bg-gradient-to-br ${b.color} rounded-xl flex items-center justify-center mb-5`}
                >
                  <i className={`${b.icon} text-white text-2xl`} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{b.title}</h3>
                <ul className="space-y-2 mb-5">
                  {b.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                      <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-arrow-right-circle-fill text-teal-500 text-lg" />
                    <p className="text-sm font-semibold text-slate-800">Ergebnis: {b.result}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revenue Models */}
      <section className="py-20 bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Zusätzliche Einnahmemodelle
          </h2>
          <p className="text-white/80 text-lg mb-12 max-w-2xl mx-auto">
            Als Learnsnap-Partner erschließen Sie sich mehrere neue, skalierbare Einnahmequellen
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {revenueModels.map((rm, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 text-white">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${rm.icon} text-2xl`} />
                </div>
                <p className="font-bold text-base mb-1">{rm.label}</p>
                <p className="text-sm text-white/70">{rm.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Models */}
      <section id="partner-models" className="py-24 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-shield-star-line" />
              Partnerschaftsmodelle
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Flexible Einsatzmöglichkeiten
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Drei Modelle für jeden Bedarf – vom einfachen Einstieg bis zur vollständig gebrandeten Lösung
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {partnerModels.map((model, i) => (
              <div
                key={i}
                className={`rounded-3xl p-8 border-2 ${model.border} bg-gradient-to-br ${model.bg} hover:shadow-2xl transition-all relative overflow-hidden`}
              >
                <div className="absolute top-4 right-4">
                  <span
                    className={`bg-gradient-to-r ${model.color} text-white text-xs font-semibold px-3 py-1 rounded-full`}
                  >
                    {model.badge}
                  </span>
                </div>
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${model.color} rounded-2xl flex items-center justify-center mb-6`}
                >
                  <i className={`${model.icon} text-white text-3xl`} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">{model.title}</h3>
                <p className="text-sm text-slate-500 mb-4 font-medium">{model.subtitle}</p>
                <p className="text-slate-700 text-sm leading-relaxed mb-6">{model.description}</p>
                <ul className="space-y-2.5 mb-6">
                  {model.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                      <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white/70 rounded-xl p-4 border border-white">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Ideal für</p>
                  <p className="text-sm font-semibold text-slate-800">{model.ideal}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Feature comparison teaser */}
          <div className="bg-white rounded-3xl border-2 border-teal-100 p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold text-slate-900 mb-4">
                  Integration in Ihr bestehendes Angebot
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Learnsnap fügt sich nahtlos in Ihre bestehende Infrastruktur ein – egal ob kleines Nachhilfeinstitut
                  oder großes Bildungsunternehmen. Sie kümmern sich um Ihre Schüler, wir kümmern uns um die Technik.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-plug-line', text: 'Schnelle Integration' },
                    { icon: 'ri-shield-check-line', text: 'DSGVO-konform' },
                    { icon: 'ri-headphone-line', text: 'Dedizierter Support' },
                    { icon: 'ri-settings-4-line', text: 'Individuell anpassbar' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                      <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                        <i className={`${item.icon} text-teal-600`} />
                      </div>
                      <span className="text-sm font-semibold text-slate-800">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=friendly%20business%20team%20two%20professionals%20one%20man%20one%20woman%20reviewing%20educational%20software%20dashboard%20on%20laptop%20together%20at%20modern%20desk%20bright%20office%20environment%20smiling%20confident%20light%20skin%20tones%20professional%20attire%20natural%20daylight%20partnership%20collaboration%20edtech%20platform%20showing%20colorful%20data%20charts&width=700&height=500&seq=partnership-integration-2025&orientation=landscape"
                  alt="Learnsnap Integration"
                  className="w-full h-72 object-cover object-top rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality & Correctness Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-shield-check-line" />
              Qualitätssicherung
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Wie Learnsnap Qualität &amp; Korrektheit sicherstellt
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Learnsnap ist kein Chatbot – sondern ein strukturiertes Lernsystem mit mehrfacher Validierung,
              Transparenz und lernbasierter Optimierung.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Point 1 – Strukturierte KI */}
            <div className="rounded-2xl p-7 border-2 border-slate-100 bg-gradient-to-br from-teal-50 to-white hover:border-teal-200 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-cpu-line text-white text-xl" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full">Integriert</span>
                  <h3 className="text-base font-bold text-slate-900 mt-0.5">Strukturierte KI statt freie Antworten</h3>
                </div>
              </div>
              <ul className="space-y-2 mb-5">
                {[
                  'Vordefinierte Lernlogiken & Antwortstrukturen',
                  'Aufgaben werden in nachvollziehbare Lösungsschritte zerlegt',
                  'Erklärt den Rechenweg / Denkprozess, nicht nur das Ergebnis',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-teal-50 rounded-xl p-3 border border-teal-100">
                <div className="flex items-center gap-2">
                  <i className="ri-arrow-right-circle-fill text-teal-500" />
                  <p className="text-xs font-semibold text-teal-800">Weniger Halluzination, mehr logische Nachvollziehbarkeit</p>
                </div>
              </div>
            </div>

            {/* Point 2 – Mehrstufige Validierung */}
            <div className="rounded-2xl p-7 border-2 border-slate-100 bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-200 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-filter-3-line text-white text-xl" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">Integriert</span>
                  <h3 className="text-base font-bold text-slate-900 mt-0.5">Mehrstufige Validierung</h3>
                </div>
              </div>
              <ul className="space-y-2 mb-5">
                {[
                  'Antworten werden intern mehrfach geprüft (Cross-Checks)',
                  'Fokus auf Plausibilität, Rechenlogik und Konsistenz',
                  'Bei Unsicherheiten wird eine Erklärung aufgebaut statt eine harte Antwort erzwungen',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                <div className="flex items-center gap-2">
                  <i className="ri-arrow-right-circle-fill text-emerald-500" />
                  <p className="text-xs font-semibold text-emerald-800">Deutlich höhere Zuverlässigkeit als einfache KI-Tools</p>
                </div>
              </div>
            </div>

            {/* Point 3 – Transparenz */}
            <div className="rounded-2xl p-7 border-2 border-slate-100 bg-gradient-to-br from-cyan-50 to-white hover:border-cyan-200 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-eye-line text-white text-xl" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-cyan-600 bg-cyan-100 px-2 py-0.5 rounded-full">Integriert</span>
                  <h3 className="text-base font-bold text-slate-900 mt-0.5">Transparenz statt Blackbox</h3>
                </div>
              </div>
              <ul className="space-y-2 mb-4">
                {[
                  'Schritt-für-Schritt-Erklärungen zur Lösung',
                  'Vollständige Herleitung jedes Ergebnisses',
                  'Alternative Denkansätze je nach Aufgabe',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <i className="ri-check-line text-cyan-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="space-y-1.5 mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Das ermöglicht:</p>
                {['Schüler erkennen Fehler selbst', 'Lehrer / Tutoren können Ergebnisse schnell prüfen'].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <i className="ri-arrow-right-s-line text-cyan-500 flex-shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Point 4 – Tutor-Integration (Optional) */}
            <div className="rounded-2xl p-7 border-2 border-dashed border-slate-200 bg-slate-50 hover:border-slate-300 transition-all relative">
              <div className="absolute top-4 right-4">
                <span className="text-xs font-semibold text-slate-500 bg-slate-200 px-2.5 py-1 rounded-full">Optional</span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-slate-400 to-slate-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-settings-line text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-700 mt-0.5">Tutor- &amp; Lehrer-Integration</h3>
                  <p className="text-xs text-slate-400 font-medium">Entscheidend für Ihre Positionierung</p>
                </div>
              </div>
              <ul className="space-y-2 mb-5">
                {[
                  'Tutoren haben Zugriff auf Ergebnisse und Lernstände ihrer Schüler',
                  'Können eingreifen, korrigieren und ergänzen',
                  'Behalten die inhaltliche Kontrolle vollständig',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-500">
                    <i className="ri-time-line text-slate-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-white rounded-xl p-3 border border-slate-200">
                <div className="flex items-center gap-2">
                  <i className="ri-information-line text-slate-400" />
                  <p className="text-xs font-semibold text-slate-500">Learnsnap = Assistenzsystem, kein Ersatz</p>
                </div>
              </div>
            </div>

            {/* Point 5 – Lernschleifen */}
            <div className="rounded-2xl p-7 border-2 border-slate-100 bg-gradient-to-br from-orange-50 to-white hover:border-orange-200 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-refresh-line text-white text-xl" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">Integriert</span>
                  <h3 className="text-base font-bold text-slate-900 mt-0.5">Lernschleifen statt einmaliger Antworten</h3>
                </div>
              </div>
              <ul className="space-y-2 mb-5">
                {[
                  'Antworten werden nicht isoliert betrachtet',
                  'System erkennt Fehlermuster & Wissenslücken',
                  'Darauf basierend entstehen gezielte Wiederholungen & Übungen',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <i className="ri-check-line text-orange-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
                <div className="flex items-center gap-2">
                  <i className="ri-arrow-right-circle-fill text-orange-500" />
                  <p className="text-xs font-semibold text-orange-800">Wissen wird aufgebaut und dauerhaft stabilisiert</p>
                </div>
              </div>
            </div>

            {/* Point 6 – System-Effekt (Optional) */}
            <div className="rounded-2xl p-7 border-2 border-dashed border-slate-200 bg-slate-50 hover:border-slate-300 transition-all relative">
              <div className="absolute top-4 right-4">
                <span className="text-xs font-semibold text-slate-500 bg-slate-200 px-2.5 py-1 rounded-full">Optional</span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-slate-400 to-slate-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-line-chart-line text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-700 mt-0.5">Qualität steigt mit Nutzung</h3>
                  <p className="text-xs text-slate-400 font-medium">System-Effekt bei wachsender Datenbasis</p>
                </div>
              </div>
              <ul className="space-y-2 mb-5">
                {[
                  'Häufige Aufgaben & Muster werden immer präziser erkannt',
                  'Inhalte werden kontinuierlich optimiert',
                  'Typische Fehler werden proaktiv adressiert',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-500">
                    <i className="ri-time-line text-slate-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-white rounded-xl p-3 border border-slate-200">
                <div className="flex items-center gap-2">
                  <i className="ri-information-line text-slate-400" />
                  <p className="text-xs font-semibold text-slate-500">Das System wird mit der Nutzung immer besser</p>
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-teal-600 bg-teal-100 px-2.5 py-1 rounded-full">Integriert</span>
              <span>Bereits vollständig in Learnsnap verfügbar</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 bg-slate-200 px-2.5 py-1 rounded-full">Optional</span>
              <span>Erweiterung je nach Partnerschaftsmodell möglich</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why now CTA band */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-300 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-fire-line" />
              Warum jetzt?
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Der richtige Moment ist jetzt
            </h2>
            <p className="text-slate-400 text-lg max-w-3xl mx-auto">
              Wir befinden uns im schnellst wachsenden Markt mit KI. Wer jetzt einsteigt, spart sich die
              eigene Entwicklung und sichert sich einen entscheidenden Vorsprung.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-time-line',
                title: 'Zeit sparen',
                desc: 'Keine eigene KI-Entwicklung nötig – nutzen Sie eine ausgereifte, bereits funktionsfähige Plattform.',
              },
              {
                icon: 'ri-money-euro-circle-line',
                title: 'Geld sparen',
                desc: 'Millionenentwicklungskosten entfallen – Sie starten als Partner sofort mit fertiger Technologie.',
              },
              {
                icon: 'ri-trophy-line',
                title: 'Vorsprung sichern',
                desc: 'Positionieren Sie sich als innovativer Bildungspartner bevor Ihre Mitbewerber reagieren.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-slate-800 rounded-2xl p-8 border border-slate-700 text-center">
                <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-teal-400 text-2xl`} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-24 bg-gradient-to-br from-teal-50 via-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold mb-4">
              <i className="ri-user-add-line" />
              Partnerschafts-Bewerbung
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Jetzt Partner werden
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Interessiert? Füllen Sie das Formular aus und wir melden uns innerhalb von 48 Stunden persönlich
              bei Ihnen für ein unverbindliches Erstgespräch.
            </p>
          </div>

          {/* Business Model Info */}
          <div className="bg-white rounded-3xl p-8 border-2 border-teal-100 mb-8">
            <div className="flex items-start gap-5 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-money-euro-circle-line text-white text-3xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">Unser Geschäftsmodell</h3>
                <p className="text-slate-500 text-sm">Transparent & individuell – passend zu Ihrem Umfang</p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6">
              Unser Geschäftsmodell basiert auf einer <strong className="text-slate-800">einmaligen Setup-Gebühr</strong> sowie
              einer <strong className="text-slate-800">monatlichen Support-Gebühr</strong>, die wir individuell je nach gewünschtem
              Umfang, Partnerschaftsmodell und Anzahl der Nutzer mit Ihnen gemeinsam festlegen. So zahlen Sie nur
              für das, was Sie wirklich brauchen – fair, transparent und skalierbar.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-teal-50 rounded-2xl p-5 border border-teal-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-tools-line text-teal-600 text-lg" />
                  </div>
                  <span className="font-bold text-slate-900">Einmalige Setup-Gebühr</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Beinhaltet Einrichtung, Branding (bei White-Label), technisches Onboarding und Schulung Ihres Teams.
                  Wird individuell je nach Umfang festgelegt.
                </p>
              </div>
              <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg">
                    <i className="ri-refresh-line text-emerald-600 text-lg" />
                  </div>
                  <span className="font-bold text-slate-900">Monatliche Support-Gebühr</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Laufende Betreuung, technischer Support, Updates und Weiterentwicklung der Plattform.
                  Staffelung nach Nutzerzahl und gewähltem Modell.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border-2 border-teal-100">
            {formStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-double-line text-teal-600 text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Bewerbung erfolgreich gesendet!</h3>
                <p className="text-slate-600 max-w-md mx-auto">
                  Vielen Dank für Ihr Interesse! Wir melden uns innerhalb von 48 Stunden persönlich bei Ihnen.
                </p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-8 bg-teal-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap"
                >
                  Weitere Anfrage senden
                </button>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Vor- und Nachname"
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-sm text-slate-900 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      E-Mail <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="ihre@email.de"
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-sm text-slate-900 transition-all"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Telefon</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Optional"
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-sm text-slate-900 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Unternehmen / Organisation</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Name Ihres Unternehmens (optional)"
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-sm text-slate-900 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Art des Bildungsangebotes <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="educationType"
                    value={formData.educationType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-sm text-slate-900 transition-all cursor-pointer"
                  >
                    <option value="">Bitte wählen...</option>
                    <option value="Nachhilfeinstitut">Nachhilfeinstitut</option>
                    <option value="Einzelnachhilfe / Tutor">Einzelnachhilfe / Tutor</option>
                    <option value="Privatschule">Privatschule</option>
                    <option value="Bildungszentrum / Akademie">Bildungszentrum / Akademie</option>
                    <option value="Online-Bildungsanbieter">Online-Bildungsanbieter</option>
                    <option value="Berufliche Weiterbildung">Berufliche Weiterbildung</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Partnerschaftsmodell <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="partnerType"
                    value={formData.partnerType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-sm text-slate-900 transition-all cursor-pointer"
                  >
                    <option value="">Bitte wählen...</option>
                    <option value="White-Label">White-Label – Eigengebrandete Version</option>
                    <option value="Franchise">Franchise – Unter Learnsnap Brand</option>
                    <option value="Partner">Partner – Nutzung für eigene Kunden</option>
                    <option value="Noch offen">Noch nicht entschieden – Erstgespräch gewünscht</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Gewünschte Funktionen
                    <span className="ml-2 text-xs font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="desiredFeatures"
                    value={formData.desiredFeatures}
                    onChange={handleInputChange}
                    placeholder="z.B. eigenes Branding, Telegram-Bot, Testvorbereitung, Schüler-Analytics ..."
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-sm text-slate-900 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Vorhandenes Budget <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    required
                    placeholder=""
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-sm text-slate-900 transition-all"
                  />
                  <p className="text-xs text-slate-400 mt-1.5">
                    Keine Sorge – das Budget ist nur zur groben Einschätzung. Wir erstellen für Sie ein individuelles Angebot.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Ihre Situation &amp; Fragen <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Beschreiben Sie kurz Ihr bestehendes Bildungsangebot, Ihre Ziele und was Sie sich von einer Partnerschaft erhoffen. (max. 500 Zeichen)"
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-sm text-slate-900 transition-all resize-none"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-slate-400">Maximal 500 Zeichen</span>
                    <span className={`text-xs font-medium ${charCount > 450 ? 'text-orange-500' : 'text-slate-400'}`}>
                      {charCount}/500
                    </span>
                  </div>
                </div>

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600 text-center">
                    Fehler beim Senden. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'sending' || charCount > 500}
                  className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-4 rounded-xl font-semibold text-base hover:shadow-xl transition-all disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'sending' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      Wird gesendet...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line" />
                      Jetzt unverbindlich anfragen
                    </span>
                  )}
                </button>
                <p className="text-center text-xs text-slate-400">
                  Unverbindlich &amp; kostenlos • Antwort innerhalb 48 Stunden • Kein Spam
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            <div>
              <Link to="/">
                <Logo size="sm" showText={true} textColor="text-white" />
              </Link>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                KI-gestützte Lernplattform für den modernen Bildungsmarkt.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-base mb-4">Navigation</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">Startseite</Link></li>
                <li><Link to="/pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Preise</Link></li>
                <li><a href="#partner-models" className="text-sm text-slate-400 hover:text-white transition-colors">Partnermodelle</a></li>
                <li><a href="#apply" className="text-sm text-slate-400 hover:text-white transition-colors">Jetzt bewerben</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-base mb-4">Rechtliches</h4>
              <ul className="space-y-2">
                <li><Link to="/datenschutz" className="text-sm text-slate-400 hover:text-white transition-colors">Datenschutz</Link></li>
                <li><Link to="/agb" className="text-sm text-slate-400 hover:text-white transition-colors">AGB</Link></li>
                <li><Link to="/impressum" className="text-sm text-slate-400 hover:text-white transition-colors">Impressum</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-500 text-sm">© 2024 Learnsnap – Future Now UG. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
